import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface FormAncestryInstance {
  instanceId: string
  entityName: string
  layoutUid?: string
  parentInstanceId: string | null
}

export interface FormAncestrySliceState {
  instances: Record<string, FormAncestryInstance>
  innermostInstanceId: string | null
  tabRootInstanceId: string | null
}

const initialState: FormAncestrySliceState = {
  instances: {},
  innermostInstanceId: null,
  tabRootInstanceId: null,
}

export interface RegisterFormInstancePayload {
  instanceId: string
  entityName: string
  layoutUid?: string
  /** When set, used instead of innermostInstanceId. Pass null for a root instance. */
  parentInstanceId?: string | null
  /** Default true. Set false for sibling tab forms so innermost stays on the root. */
  updateInnermost?: boolean
  /** First ROOT tab form (case); skips duplicate ROOT registrations. */
  setAsTabRoot?: boolean
  /** Tab child form; parents to tabRootInstanceId, updateInnermost false. */
  registerAsTabChild?: boolean
}

const formAncestrySlice = createSlice({
  name: 'formAncestry',
  initialState,
  reducers: {
    registerFormInstance: (
      state,
      action: PayloadAction<RegisterFormInstancePayload>
    ) => {
      const {
        instanceId,
        entityName,
        layoutUid,
        parentInstanceId: explicitParentId,
        updateInnermost = true,
        setAsTabRoot,
        registerAsTabChild,
      } = action.payload

      if (setAsTabRoot) {
        if (state.tabRootInstanceId) return
        state.instances[instanceId] = {
          instanceId,
          entityName,
          layoutUid,
          parentInstanceId: null,
        }
        state.tabRootInstanceId = instanceId
        state.innermostInstanceId = instanceId
        return
      }

      if (registerAsTabChild) {
        if (!state.tabRootInstanceId) return
        state.instances[instanceId] = {
          instanceId,
          entityName,
          layoutUid,
          parentInstanceId: state.tabRootInstanceId,
        }
        return
      }

      const parentInstanceId =
        explicitParentId !== undefined
          ? explicitParentId
          : state.innermostInstanceId
      state.instances[instanceId] = {
        instanceId,
        entityName,
        layoutUid,
        parentInstanceId,
      }
      if (updateInnermost) {
        state.innermostInstanceId = instanceId
      }
    },
    unregisterFormInstance: (state, action: PayloadAction<string>) => {
      const instanceId = action.payload
      const instance = state.instances[instanceId]
      if (!instance) return

      delete state.instances[instanceId]

      if (state.tabRootInstanceId === instanceId) {
        state.tabRootInstanceId = null
      }

      if (state.innermostInstanceId === instanceId) {
        state.innermostInstanceId = instance.parentInstanceId
      }
    },
  },
})

export const { registerFormInstance, unregisterFormInstance } =
  formAncestrySlice.actions

export default formAncestrySlice.reducer

export interface FormAncestryRootState {
  formAncestry: FormAncestrySliceState
}

export const selectTabRootInstanceId = (
  state: FormAncestryRootState
): string | null => state.formAncestry.tabRootInstanceId

export const selectFormAncestryInstance = (
  state: FormAncestryRootState,
  instanceId: string
): FormAncestryInstance | undefined => state.formAncestry.instances[instanceId]

export const selectAncestorInstances = (
  state: FormAncestryRootState,
  instanceId: string
): FormAncestryInstance[] => {
  const ancestors: FormAncestryInstance[] = []
  const current = state.formAncestry.instances[instanceId]
  if (!current) return ancestors

  let parentId = current.parentInstanceId
  while (parentId) {
    const parent = state.formAncestry.instances[parentId]
    if (!parent) break
    ancestors.unshift(parent)
    parentId = parent.parentInstanceId
  }
  return ancestors
}
